import { useEffect, useState, useRef } from 'preact/hooks';
import { MainLayout } from '../../components/MainLayout';
import { useScopeContext } from '../../contexts/ScopeContext';
import {
  teamFilesService,
  TeamFileListItem,
  TeamFileEntity,
  FILE_CONSTRAINTS,
  ContextUsage,
} from '../../services/team-files.service';
import { toast } from 'react-toastify';

type SortField = 'name' | 'uploadedAt' | 'sizeBytes';
type SortDirection = 'asc' | 'desc';

export function TeamFilesPage() {
  const scope = useScopeContext();
  const [files, setFiles] = useState<TeamFileListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Detail modal state
  const [selectedFile, setSelectedFile] = useState<TeamFileEntity | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<TeamFileListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Sort and filter state
  const [sortField, setSortField] = useState<SortField>('uploadedAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchQuery, setSearchQuery] = useState('');

  // Regenerate summary state
  const [regenerating, setRegenerating] = useState(false);

  // Edit summary state
  const [editingSummary, setEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);

  // Context usage state
  const [contextUsage, setContextUsage] = useState<ContextUsage | undefined>();

  // Toggle alwaysInContext state
  const [togglingContext, setTogglingContext] = useState<string | null>(null);

  // Create from text inline form state
  const [showPasteForm, setShowPasteForm] = useState(false);
  const [createFilename, setCreateFilename] = useState('');
  const [createContent, setCreateContent] = useState('');

  useEffect(() => {
    loadFiles();
  }, [scope.teamId]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await teamFilesService.listFiles(scope.teamId);
      setFiles(result.files);
      setContextUsage(result.contextUsage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  // Sorting and filtering
  const sortedAndFilteredFiles = files
    .filter((f) => {
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        f.name.toLowerCase().includes(query) ||
        f.slug.toLowerCase().includes(query) ||
        f.id.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      let cmp = 0;
      if (sortField === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortField === 'uploadedAt') {
        cmp = new Date(a.uploadedAt).getTime() - new Date(b.uploadedAt).getTime();
      } else if (sortField === 'sizeBytes') {
        cmp = a.sizeBytes - b.sizeBytes;
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });

  // File upload handling
  const handleFileSelect = (e: Event) => {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      uploadFile(input.files[0]);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
      uploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const uploadFile = async (file: File) => {
    // Validate file
    const validation = teamFilesService.validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Simulate progress for better UX (actual upload doesn't give progress)
    const progressInterval = setInterval(() => {
      setUploadProgress((p) => Math.min(p + 10, 90));
    }, 200);

    try {
      const result = await teamFilesService.uploadFile(scope.teamId, file);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success && result.data) {
        toast.success(`File uploaded! Slug: ${result.data.id}`);
        loadFiles();
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (err) {
      clearInterval(progressInterval);
      toast.error(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Create file from text
  const handleCreateFromText = async () => {
    // Validate filename
    let filename = createFilename.trim();
    if (!filename) {
      toast.error('Please enter a filename');
      return;
    }

    // Ensure proper extension
    if (!filename.endsWith('.md') && !filename.endsWith('.txt')) {
      filename += '.md'; // Default to markdown
    }

    // Validate content
    if (!createContent.trim()) {
      toast.error('Please enter some content');
      return;
    }

    // Determine mime type
    const mimeType = filename.endsWith('.md') ? 'text/markdown' : 'text/plain';

    // Create a File object from the text
    const blob = new Blob([createContent], { type: mimeType });
    const file = new File([blob], filename, { type: mimeType });

    // Validate file size
    const validation = teamFilesService.validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    // Hide form and upload
    setShowPasteForm(false);
    await uploadFile(file);

    // Reset form
    setCreateFilename('');
    setCreateContent('');
  };

  const closePasteForm = () => {
    setShowPasteForm(false);
    setCreateFilename('');
    setCreateContent('');
  };

  // File detail modal
  const openFileDetail = async (file: TeamFileListItem) => {
    setLoadingDetail(true);
    setShowDetailModal(true);

    try {
      const detail = await teamFilesService.getFile(scope.teamId, file.id);
      setSelectedFile(detail);
      setEditedSummary(detail?.summary || '');
    } catch (err) {
      toast.error('Failed to load file details');
      setShowDetailModal(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedFile(null);
    setEditingSummary(false);
    setEditedSummary('');
  };

  // Copy slug to clipboard
  const copySlug = (slug: string) => {
    navigator.clipboard.writeText(slug);
    toast.success(`Copied ${slug}`);
  };

  // Download file
  const handleDownload = async () => {
    if (!selectedFile) return;
    try {
      await teamFilesService.downloadFile(scope.teamId, selectedFile.id, selectedFile.name);
      toast.success('Download started');
    } catch (err) {
      toast.error('Download failed');
    }
  };

  // Regenerate summary
  const handleRegenerateSummary = async () => {
    if (!selectedFile) return;
    setRegenerating(true);
    try {
      const result = await teamFilesService.regenerateSummary(scope.teamId, selectedFile.id);
      if (result.success && result.data) {
        setSelectedFile((f) => (f ? { ...f, summary: result.data!.summary, status: 'ready' } : null));
        setEditedSummary(result.data.summary);
        toast.success('Summary regenerated');
        loadFiles();
      } else {
        toast.error(result.error || 'Failed to regenerate summary');
      }
    } catch (err) {
      toast.error('Failed to regenerate summary');
    } finally {
      setRegenerating(false);
    }
  };

  // Save edited summary
  const handleSaveSummary = async () => {
    if (!selectedFile) return;
    setSavingSummary(true);
    try {
      const result = await teamFilesService.updateFile(scope.teamId, selectedFile.id, {
        summary: editedSummary,
      });
      if (result.success && result.data) {
        setSelectedFile(result.data);
        setEditingSummary(false);
        toast.success('Summary updated');
      } else {
        toast.error(result.error || 'Failed to update summary');
      }
    } catch (err) {
      toast.error('Failed to update summary');
    } finally {
      setSavingSummary(false);
    }
  };

  // Toggle always in context
  const handleToggleAlwaysInContext = async (fileId: string, currentValue: boolean) => {
    setTogglingContext(fileId);
    try {
      const result = await teamFilesService.updateFile(scope.teamId, fileId, {
        alwaysInContext: !currentValue,
      });
      if (result.success) {
        // Update local state
        setFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, alwaysInContext: !currentValue } : f))
        );
        if (selectedFile?.id === fileId) {
          setSelectedFile((f) => (f ? { ...f, alwaysInContext: !currentValue } : null));
        }
        // Reload to get updated context usage
        loadFiles();
        toast.success(!currentValue ? 'File will be included in agent context' : 'File removed from agent context');
      } else {
        toast.error(result.error || 'Failed to update file');
      }
    } catch (err) {
      toast.error('Failed to update file');
    } finally {
      setTogglingContext(null);
    }
  };

  // Delete file
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await teamFilesService.deleteFile(scope.teamId, deleteTarget.id);
      if (result.success) {
        toast.success('File deleted');
        setDeleteTarget(null);
        if (selectedFile?.id === deleteTarget.id) {
          closeDetailModal();
        }
        loadFiles();
      } else {
        toast.error(result.error || 'Failed to delete file');
      }
    } catch (err) {
      toast.error('Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  // Format date
  const formatDate = (iso: string) => {
    const date = new Date(iso);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Render paste form (shared between empty and non-empty states)
  const renderPasteForm = (isEmptyState: boolean) => (
    <div className={`border-2 border-base-300 rounded-xl p-6 ${isEmptyState ? 'max-w-2xl mx-auto' : 'mb-4'}`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className={`font-semibold ${isEmptyState ? 'text-xl' : ''}`}>Create File from Text</h3>
        <button className="btn btn-ghost btn-sm" onClick={closePasteForm}>
          Cancel
        </button>
      </div>

      {isEmptyState && (
        <p className="text-base-content/70 mb-4">
          Paste content from Coda, Google Docs, or any other source.
        </p>
      )}

      <div className="mb-4">
        <label className="label">
          <span className="label-text font-medium">Filename</span>
        </label>
        <input
          type="text"
          placeholder="e.g., brand-guidelines.md"
          className="input input-bordered w-full max-w-md"
          value={createFilename}
          onInput={(e) => setCreateFilename((e.target as HTMLInputElement).value)}
        />
        <p className="text-xs text-base-content/50 mt-1 ml-1">.md will be added if no extension provided</p>
      </div>

      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <label className="label py-0">
            <span className="label-text font-medium">Content</span>
          </label>
          <span className="text-xs text-base-content/50">
            {createContent.length > 0
              ? `${createContent.length.toLocaleString()} characters`
              : `Max ${(FILE_CONSTRAINTS.MAX_SIZE_BYTES / 1024).toFixed(0)}KB`}
          </span>
        </div>
        <textarea
          className={`textarea textarea-bordered w-full font-mono text-sm ${isEmptyState ? 'h-64' : 'h-48'}`}
          placeholder="Paste content from Coda, Google Docs, or any other source..."
          value={createContent}
          onInput={(e) => setCreateContent((e.target as HTMLTextAreaElement).value)}
        />
      </div>

      <div className="flex gap-2">
        <button
          className="btn btn-primary"
          onClick={handleCreateFromText}
          disabled={!createFilename.trim() || !createContent.trim() || uploading}
        >
          {uploading ? 'Creating...' : 'Create File'}
        </button>
        <button className="btn btn-ghost" onClick={closePasteForm}>
          Cancel
        </button>
      </div>
    </div>
  );

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
        return <span className="badge badge-success badge-sm">Ready</span>;
      case 'pending':
      case 'processing':
        return <span className="badge badge-warning badge-sm">Processing</span>;
      case 'failed':
        return <span className="badge badge-error badge-sm">Failed</span>;
      default:
        return <span className="badge badge-ghost badge-sm">{status}</span>;
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div class="flex-1 overflow-auto">
          <div className="container mx-auto p-4">
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div class="flex-1 overflow-auto">
        <div className="container mx-auto p-4">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold">Team Files</h1>
            <p className="text-base-content/70 mt-1">
              Upload files to make them searchable by your AI agents
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={FILE_CONSTRAINTS.ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileSelect}
          />

          {/* Upload Progress */}
          {uploading && (
            <div className="mb-4">
              <progress className="progress progress-primary w-full" value={uploadProgress} max="100"></progress>
            </div>
          )}

          {/* Context Usage Stats */}
          {contextUsage && (
            <div className={`alert mb-4 ${
              contextUsage.files >= contextUsage.maxFiles || contextUsage.tokens >= contextUsage.maxTokens
                ? 'alert-warning'
                : 'alert-info'
            }`}>
              <span>
                Always in Context: {contextUsage.files}/{contextUsage.maxFiles} files ({contextUsage.tokens.toLocaleString()}/{contextUsage.maxTokens.toLocaleString()} tokens)
                {(contextUsage.files >= contextUsage.maxFiles || contextUsage.tokens >= contextUsage.maxTokens) && (
                  <span className="ml-2 font-semibold">- Limit reached</span>
                )}
              </span>
            </div>
          )}

          {/* Empty State */}
          {files.length === 0 ? (
            showPasteForm ? (
              renderPasteForm(true)
            ) : (
              /* Drag & Drop Zone - Empty State */
              <div
                className={`border-2 border-dashed rounded-2xl p-12 text-center transition-colors ${
                  dragActive ? 'border-primary bg-primary/10' : 'border-base-300'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="text-6xl mb-4">📄</div>
                <h3 className="text-2xl font-semibold mb-2">No team files yet</h3>
                <p className="text-base-content/70 mb-4 max-w-md mx-auto">
                  Upload files to make them searchable by your AI agents. Files are summarized
                  automatically and can be referenced in workflows using their slug (e.g., #blog-rules).
                </p>
                <p className="text-sm text-base-content/50 mb-6">
                  Supported formats: {FILE_CONSTRAINTS.ALLOWED_EXTENSIONS.join(', ')} (max{' '}
                  {FILE_CONSTRAINTS.MAX_SIZE_BYTES / (1024 * 1024)}MB)
                </p>
                <div className="flex gap-2 justify-center">
                  <button
                    className="btn btn-primary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    Upload File
                  </button>
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowPasteForm(true)}
                    disabled={uploading}
                  >
                    Paste Text
                  </button>
                </div>
              </div>
            )
          ) : (
            <>
              {/* Search and Sort Controls */}
              <div className="flex gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Search files..."
                  className="input input-bordered flex-1"
                  value={searchQuery}
                  onInput={(e) => setSearchQuery((e.target as HTMLInputElement).value)}
                />
                <select
                  className="select select-bordered"
                  value={`${sortField}-${sortDirection}`}
                  onChange={(e) => {
                    const [field, dir] = (e.target as HTMLSelectElement).value.split('-');
                    setSortField(field as SortField);
                    setSortDirection(dir as SortDirection);
                  }}
                >
                  <option value="uploadedAt-desc">Newest first</option>
                  <option value="uploadedAt-asc">Oldest first</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="sizeBytes-desc">Size (largest)</option>
                  <option value="sizeBytes-asc">Size (smallest)</option>
                </select>
              </div>

              {/* Upload Zone */}
              {showPasteForm ? (
                renderPasteForm(false)
              ) : (
                /* Drag and Drop Zone */
                <div
                  className={`border-2 border-dashed rounded-xl p-4 mb-4 text-center transition-colors ${
                    dragActive ? 'border-primary bg-primary/10' : 'border-base-300'
                  }`}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <p className="text-base-content/70">
                    Drop files here to upload, or{' '}
                    <button
                      className="link link-primary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      browse
                    </button>
                    {' '}&bull;{' '}
                    <button
                      className="link link-primary"
                      onClick={() => setShowPasteForm(true)}
                    >
                      paste text
                    </button>
                  </p>
                </div>
              )}

              {/* File List */}
              <div className="grid gap-4">
                {sortedAndFilteredFiles.map((file) => (
                  <div
                    key={file.id}
                    className="card bg-base-100 shadow hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => openFileDetail(file)}
                  >
                    <div className="card-body py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {file.alwaysInContext && (
                              <span className="text-lg" title="Always in context">📌</span>
                            )}
                            <h3 className="font-semibold truncate">{file.name}</h3>
                            {getStatusBadge(file.status)}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-base-content/70">
                            <button
                              className="link link-primary font-mono"
                              onClick={(e) => {
                                e.stopPropagation();
                                copySlug(file.id);
                              }}
                              title="Click to copy"
                            >
                              {file.id}
                            </button>
                            <span>{teamFilesService.formatFileSize(file.sizeBytes)}</span>
                            <span>{formatDate(file.uploadedAt)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className={`btn btn-ghost btn-sm ${file.alwaysInContext ? 'text-primary' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleAlwaysInContext(file.id, file.alwaysInContext);
                            }}
                            disabled={togglingContext === file.id}
                            title={file.alwaysInContext ? 'Remove from agent context' : 'Always include in agent context'}
                          >
                            {togglingContext === file.id ? (
                              <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                              file.alwaysInContext ? '📌' : '📍'
                            )}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm text-error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(file);
                            }}
                            title="Delete"
                          >
                            Delete
                          </button>
                          <span className="text-2xl opacity-50">→</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* File Detail Modal */}
      {showDetailModal && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-2xl">
            {loadingDetail ? (
              <div className="flex items-center justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
              </div>
            ) : selectedFile ? (
              <>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="font-bold text-lg">{selectedFile.name}</h3>
                  <button className="btn btn-sm btn-circle btn-ghost" onClick={closeDetailModal}>
                    ✕
                  </button>
                </div>

                {/* Slug */}
                <div className="mb-4">
                  <label className="text-sm text-base-content/70">Slug</label>
                  <div className="flex items-center gap-2">
                    <code className="font-mono text-lg">{selectedFile.id}</code>
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={() => copySlug(selectedFile.id)}
                    >
                      Copy
                    </button>
                  </div>
                </div>

                {/* Usage Hint */}
                <div className="alert alert-info mb-4">
                  <span>
                    Reference this file in workflows:{' '}
                    <code className="font-mono">"Search knowledge base for {selectedFile.id}"</code>
                  </span>
                </div>

                {/* Summary */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm text-base-content/70">Summary</label>
                    {!editingSummary && (
                      <button
                        className="btn btn-ghost btn-xs"
                        onClick={() => {
                          setEditingSummary(true);
                          setEditedSummary(selectedFile.summary);
                        }}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {editingSummary ? (
                    <div>
                      <textarea
                        className="textarea textarea-bordered w-full h-48"
                        value={editedSummary}
                        onInput={(e) => setEditedSummary((e.target as HTMLTextAreaElement).value)}
                      />
                      <div className="flex gap-2 mt-2">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleSaveSummary}
                          disabled={savingSummary}
                        >
                          {savingSummary ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setEditingSummary(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-base-200 rounded-lg p-4 max-h-48 overflow-auto">
                      <p className="whitespace-pre-wrap">{selectedFile.summary || 'No summary available'}</p>
                    </div>
                  )}
                </div>

                {/* Always in Context Toggle */}
                <div className="mb-4 p-4 bg-base-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-medium">Always in Context</label>
                      <p className="text-sm text-base-content/70 mt-1">
                        When enabled, this file's summary is automatically included in every agent message.
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      className="toggle toggle-primary"
                      checked={selectedFile.alwaysInContext}
                      onChange={() => handleToggleAlwaysInContext(selectedFile.id, selectedFile.alwaysInContext)}
                      disabled={togglingContext === selectedFile.id}
                    />
                  </div>
                </div>

                {/* Metadata */}
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <label className="text-base-content/70">Uploaded</label>
                    <p>{formatDate(selectedFile.uploadedAt)}</p>
                  </div>
                  <div>
                    <label className="text-base-content/70">Size</label>
                    <p>{teamFilesService.formatFileSize(selectedFile.sizeBytes)}</p>
                  </div>
                  <div>
                    <label className="text-base-content/70">Type</label>
                    <p>{selectedFile.mimeType}</p>
                  </div>
                  <div>
                    <label className="text-base-content/70">Status</label>
                    <p>{getStatusBadge(selectedFile.status)}</p>
                  </div>
                </div>

                {/* Error Message */}
                {selectedFile.errorMessage && (
                  <div className="alert alert-error mb-4">
                    <span>{selectedFile.errorMessage}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <button className="btn btn-outline btn-sm" onClick={handleDownload}>
                    Download Original
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={handleRegenerateSummary}
                    disabled={regenerating}
                  >
                    {regenerating ? 'Regenerating...' : 'Regenerate Summary'}
                  </button>
                </div>

                <div className="divider"></div>

                <button
                  className="btn btn-error btn-outline btn-sm"
                  onClick={() => setDeleteTarget(selectedFile as any)}
                >
                  Delete File
                </button>
              </>
            ) : (
              <p>File not found</p>
            )}
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={closeDetailModal}>close</button>
          </form>
        </dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Delete file?</h3>
            <p className="py-4">
              Are you sure you want to delete "{deleteTarget.name}"?
            </p>
            <p className="text-base-content/70 text-sm">
              This will remove the file from your team's knowledge base. Agents will no longer be
              able to search for {deleteTarget.id}.
            </p>
            <div className="modal-action">
              <button className="btn btn-ghost" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="btn btn-error" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={() => setDeleteTarget(null)}>close</button>
          </form>
        </dialog>
      )}
    </MainLayout>
  );
}
